import type { IconIds } from 'src/common/components/IconDS22';
import type { Amounts } from 'src/common/types/common/Amounts';
import type { Currency } from 'src/common/types/common/Currency';

export const ADDON_PAYMENT_DUE = {
  PAID: 0,
  /** Paid by renter at initial checkout, or via charge-addons  */
  BEFORE_BOOKING: 1,
  /** @deprecated by backend */
  BEFORE_TRIP: 2,
  /** Shown to renter before booking as a warning that they will be charged after the trip */
  AFTER_TRIP: 3,
} as const;
export type AddonPaymentDue = typeof ADDON_PAYMENT_DUE[keyof typeof ADDON_PAYMENT_DUE];

export const ADDON_SUB_TYPE = {
  EQUIPMENT: 1,
  SERVICES: 2,
  HOURS: 3,
  CATERING: 4,
  FUEL: 5,
  TIP: 6,
  INSTALLMENT: 7,
  ITINERARY: 8,
  OTHER: 9,
} as const;
export type AddonSubType = typeof ADDON_SUB_TYPE[keyof typeof ADDON_SUB_TYPE];

export const ADDON_RATE_UNIT = {
  PER_HOUR: 1,
  PER_TRIP: 2,
  PER_PERSON: 3,
  PER_UNIT: 4,
  PERCENTAGE_OF_TRIP_COST: 5,
} as const;
export type AddonRateUnit = typeof ADDON_RATE_UNIT[keyof typeof ADDON_RATE_UNIT];

type BaseAddon = {
  sub_type: AddonSubType;
  title: string;
  description: string;
  optional: boolean;
  payment_due: AddonPaymentDue;
  /** `number` if `rate_unit === 4` (per unit), else `null` */
  inventory: number | null;
  rate_units: AddonRateUnit;
  /** The cost 0 for `sub_type === 5` (Percentage of trip)  */
  amount: number;
  /** Non-zero if `sub_type === 5` (Percentage of trip), else `0.0` */
  percentage: number;
  icon_name: IconIds;
  currency: Currency;
  id: number;
};

// --------- BOATS API --------- //

/**
 * To retreive all addons for a listing.
 * (not a subset of listings addons associated with a trip (thread).)
 * - `/boats/{listing_id}/addons/`
 * - `/boats/{listing_id}/addons/optional/`
 * Swagger model `OwnerAddon`
 */
export type ListingAddon = BaseAddon & {
  /** Owner id */
  user_id: number;
};

// --------- TRIPS API --------- //

/**
 * `/api/v4/trips/{trip_id}/thread-addons/`
 * Not all addons that exist on a listing will be associated with a trip (thread).
 * Django model `ThreadAddon`
 */
export type TripAddon = BaseAddon & {
  /** Translated AddonSubType, e.g. `Equipment` | `Services` | `Equipo` | `Servicios` */
  sub_type_name: string;
  quantity: number;
  /**
   * Initializes as `false`. To make `true`, request a followup PATCH to
   * `/api/v4/trips/${trip.id}/thread-addons/${addon.id}/` with body `{ selected }`
   */
  selected: boolean;
};

/**
 * Response from PATCH to `/api/v4/trips/${trip.id}/thread-addons/${addon.id}/`
 * Used to select, deselect, or update the quantity of an addon for a specific trip (thread).
 */
export type TripAddonPatchResponse = {
  /** The addon with updated values (i.e. selected or inventory could have changed) */
  thread_addon: TripAddon;
  amounts: Amounts;
};

/** For apis /user/owner-addons/ */
export type OwnerAddon = {
  id: number;
  user_id: number;
  sub_type: AddonSubType;
  icon_name: IconIds;
  title: string;
  description: string;
  currency: Currency['code'];
  optional: boolean;
  payment_due: AddonPaymentDue;
  rate_units: AddonRateUnit;
  amount: number;
  percentage: number;
  inventory: number | null;
  boats: number[];
};
